import type { ChatMessage } from '../types/chat'
import {
  applyReminderPreviewInactiveBeforeYesNoFollowUp,
  userTextIsReminderPreviewFollowUp,
} from './reminderPreviewFollowupText'
import {
  userTextIsSecretaryPreviewFollowUp,
  userTextIsSecretarySendDecline,
  userTextIsSecretaryEditCommand,
  isSecretaryPreviewActionable,
} from './secretaryPreviewFollowupText'

/** Assistant row that is clearly not “stay on this reminder confirmation card only”. */
function assistantHasNonReminderUi(m: ChatMessage): boolean {
  if (m.role !== 'assistant') return false
  return Boolean(
    m.openPatientDossierUrl ||
      m.openNavigationUrl ||
      m.navigationMeta ||
      m.modalityMeta ||
      m.autoOpenModalityFile ||
      m.autoOpenPrescriptionDocument ||
      m.prescriptionDocumentPreviewUrl ||
      m.prescriptionMeta ||
      m.renewOrdonnanceMeta ||
      m.secretaryMessage ||
      m.mailDocument ||
      m.unreadMessagesUi ||
      m.closeModalityPreview ||
      m.uiType === 'patient_options' ||
      m.uiType === 'recipient_type_options' ||
      m.uiType === 'delivery_channel_options' ||
      m.uiType === 'prescription_type_options' ||
      m.sportsNocSummary ||
      m.sportsNocYesNoPrompt ||
      m.sportsNocFlowTrigger ||
      m.awaitingGeneralConfirmation ||
      m.awaitingMessageConfirmation ||
      m.awaitingMailConfirmation,
  )
}

/**
 * Consecutive user messages at the end of history (after the last assistant row).
 * Used so a new question typed before the assistant replies still disables reminder Yes/No.
 */
export function collectTrailingUserMessages(prev: ChatMessage[]): ChatMessage[] {
  const out: ChatMessage[] = []
  for (let i = prev.length - 1; i >= 0; i--) {
    if (prev[i].role === 'assistant') break
    if (prev[i].role === 'user') out.unshift(prev[i])
  }
  return out
}

/**
 * True when new assistant message(s) mean any earlier “awaiting reminder confirmation”
 * Yes/No row should no longer be actionable (other intent, or a newer reminder preview).
 */
export function shouldSupersedeReminderYesNoFromAssistants(incoming: ChatMessage[]): boolean {
  for (const m of incoming) {
    if (m.role !== 'assistant') continue
    if (m.reminderConfirmed) return true
    if (m.reminderPreview && m.awaitingRemindConfirmation) return true
    if (assistantHasNonReminderUi(m)) return true
  }
  return false
}

function shouldSupersedeReminderYesNoContext(prev: ChatMessage[], incoming: ChatMessage[]): boolean {
  if (shouldSupersedeReminderYesNoFromAssistants(incoming)) return true
  for (const m of incoming) {
    if (m.role === 'user' && !userTextIsReminderPreviewFollowUp(m.content)) return true
  }
  for (const m of collectTrailingUserMessages(prev)) {
    if (m.role === 'user' && !userTextIsReminderPreviewFollowUp(m.content)) return true
  }
  return false
}

/** Mark earlier awaiting-reminder previews inactive (hide Yes/No and Edit). */
export function applyReminderYesNoSupersedeToEarlierPreviews(
  prev: ChatMessage[],
  incoming: ChatMessage[],
): ChatMessage[] {
  if (!shouldSupersedeReminderYesNoContext(prev, incoming)) return prev
  return prev.map((m) => {
    if (
      m.role === 'assistant' &&
      m.reminderPreview &&
      m.awaitingRemindConfirmation &&
      !m.reminderPreviewResolved &&
      !m.reminderConfirmed &&
      !m.reminderYesNoSuperseded
    ) {
      return {
        ...m,
        awaitingRemindConfirmation: false,
        reminderPreviewResolved: true,
        reminderYesNoSuperseded: true,
      }
    }
    return m
  })
}

/**
 * When a separate success/cancel assistant row arrives, lock earlier preview cards
 * (hide actions) without turning them into outcome/success UI.
 */
export function applyReminderConfirmedToEarlierPreviews(
  prev: ChatMessage[],
  incoming: ChatMessage[],
): ChatMessage[] {
  const hasOutcome = incoming.some(
    (m) => m.role === 'assistant' && (m.reminderConfirmed || m.reminderCancelled),
  )
  if (!hasOutcome) return prev
  return prev.map((m) => {
    if (
      m.role === 'assistant' &&
      m.reminderPreview &&
      !m.reminderPreviewResolved &&
      !m.reminderCancelled
    ) {
      return {
        ...m,
        awaitingRemindConfirmation: false,
        reminderPreviewResolved: true,
        reminderYesNoSuperseded: true,
      }
    }
    return m
  })
}

function messageHasPendingSecretarySendConfirm(m: ChatMessage): boolean {
  if (m.role !== 'assistant' || !m.secretaryMessage) return false
  if (m.secretaryPreviewSuperseded) return false
  return isSecretaryPreviewActionable(m)
}

function assistantShouldSupersedeSecretaryPreview(m: ChatMessage): boolean {
  if (m.role !== 'assistant') return false
  if (messageHasPendingSecretarySendConfirm(m)) return true
  if (m.reminderPreview || m.awaitingRemindConfirmation) return true
  if (m.secretaryMessage) {
    const note = (m.secretaryMessage.autofill_note || '').trim()
    if (note && note !== 'awaiting_send_confirmation') return true
    if ((m.secretaryMessage.message_sent_status || '').trim()) return true
  }
  return Boolean(
    m.openPatientDossierUrl ||
      m.openNavigationUrl ||
      m.navigationMeta ||
      m.modalityMeta ||
      m.autoOpenModalityFile ||
      m.autoOpenPrescriptionDocument ||
      m.prescriptionDocumentPreviewUrl ||
      m.prescriptionMeta ||
      m.renewOrdonnanceMeta ||
      m.mailDocument ||
      m.unreadMessagesUi ||
      m.closeModalityPreview ||
      m.uiType === 'patient_options' ||
      m.uiType === 'prescription_type_options' ||
      m.uiType === 'recipient_type_options' ||
      m.uiType === 'delivery_channel_options' ||
      m.sportsNocSummary ||
      m.sportsNocYesNoPrompt ||
      m.sportsNocFlowTrigger ||
      m.awaitingGeneralConfirmation ||
      m.awaitingMailConfirmation,
  )
}

function shouldSupersedeSecretaryPreviewFromAssistants(incoming: ChatMessage[]): boolean {
  return incoming.some((m) => assistantShouldSupersedeSecretaryPreview(m))
}

function shouldSupersedeSecretaryPreviewContext(
  prev: ChatMessage[],
  incoming: ChatMessage[],
): boolean {
  if (shouldSupersedeSecretaryPreviewFromAssistants(incoming)) return true
  const history = [...prev, ...incoming]
  for (const m of incoming) {
    if (m.role === 'user' && userTextIsSecretarySendDecline(m.content)) return true
    if (m.role === 'user' && !userTextIsSecretaryPreviewFollowUp(m.content, history)) return true
  }
  for (const m of collectTrailingUserMessages(prev)) {
    if (m.role === 'user' && userTextIsSecretarySendDecline(m.content)) return true
    if (m.role === 'user' && !userTextIsSecretaryPreviewFollowUp(m.content, history)) return true
  }
  return false
}

/** Disable Edit / send Yes-No on earlier secretary previews when the conversation moves on. */
export function applySecretaryPreviewSupersedeToEarlierPreviews(
  prev: ChatMessage[],
  incoming: ChatMessage[],
): ChatMessage[] {
  if (!shouldSupersedeSecretaryPreviewContext(prev, incoming)) return prev
  return prev.map((m) => {
    if (!messageHasPendingSecretarySendConfirm(m)) return m
    const sm = m.secretaryMessage ? { ...m.secretaryMessage } : undefined
    if (sm?.autofill_note === 'awaiting_send_confirmation') {
      delete sm.autofill_note
    }
    return {
      ...m,
      awaitingMessageConfirmation: false,
      secretaryPreviewSuperseded: true,
      secretaryMessage: sm,
    }
  })
}

function messageHasPendingMailDocumentConfirm(m: ChatMessage): boolean {
  if (m.role !== 'assistant') return false
  return m.awaitingMailConfirmation === true || m.mailDocument?.awaitingMailConfirmation === true
}

function incomingClosesMailDocumentConfirmation(incoming: ChatMessage[]): boolean {
  for (const m of incoming) {
    if (m.role !== 'assistant') continue
    const md = m.mailDocument
    if (!md) continue
    const st = (md.mail_sent_status || '').trim().toLowerCase()
    if (
      st === 'success' ||
      st === 'sent' ||
      st === 'failure' ||
      st === 'cancelled' ||
      st === 'invalidated'
    ) {
      return true
    }
  }
  return false
}

function shouldSupersedeMailDocumentAwaitingContext(
  prev: ChatMessage[],
  incoming: ChatMessage[],
): boolean {
  if (!prev.some(messageHasPendingMailDocumentConfirm)) return false
  return incomingClosesMailDocumentConfirmation(incoming)
}

function incomingHasMailDocumentSendPreview(incoming: ChatMessage[]): boolean {
  return incoming.some(
    (m) =>
      m.role === 'assistant' &&
      !m.historySnapshot &&
      (m.awaitingMailConfirmation === true || m.mailDocument?.awaitingMailConfirmation === true),
  )
}

/** Blood-test / ordonnance row rendered in the preview iframe — open-then-mail can advance. */
function incomingHasRenderablePrescriptionDocumentForMailOpen(incoming: ChatMessage[]): boolean {
  return incoming.some(
    (m) =>
      m.role === 'assistant' &&
      !m.historySnapshot &&
      m.autoOpenPrescriptionDocument === true &&
      Boolean((m.prescriptionDocumentPreviewUrl ?? '').trim()),
  )
}

/** Any mail follow-up after the open step (preview, error, cancel) — not still waiting on open. */
function incomingHasMailDocumentOpenFollowUp(incoming: ChatMessage[]): boolean {
  return incoming.some((m) => {
    if (m.role !== 'assistant' || m.historySnapshot) return false
    const md = m.mailDocument
    if (!md) return false
    return md.awaitingDocumentOpen !== true
  })
}

function incomingClosesMailDocumentOpenWait(incoming: ChatMessage[]): boolean {
  return (
    incomingHasMailDocumentSendPreview(incoming) ||
    incomingHasRenderablePrescriptionDocumentForMailOpen(incoming) ||
    incomingHasMailDocumentOpenFollowUp(incoming)
  )
}

/** Clear ``awaitingDocumentOpen`` on older rows once mail send preview arrives (open-then-mail completed). */
export function applyMailDocumentOpenAwaitingSupersedeToEarlierPreviews(
  prev: ChatMessage[],
  incoming: ChatMessage[],
): ChatMessage[] {
  if (!incomingClosesMailDocumentOpenWait(incoming)) return prev
  let changed = false
  const next = prev.map((m) => {
    if (m.role !== 'assistant' || m.historySnapshot) return m
    if (m.mailDocument?.awaitingDocumentOpen !== true) return m
    changed = true
    const md = m.mailDocument
    return {
      ...m,
      mailDocument: md ? { ...md, awaitingDocumentOpen: false } : md,
    }
  })
  return changed ? next : prev
}

/** Turn off mail-document send confirmation on older rows once a success/failure row arrives (see ``findPendingBinaryConfirm`` tail scan). */
export function applyMailDocumentAwaitingSupersedeToEarlierPreviews(
  prev: ChatMessage[],
  incoming: ChatMessage[],
): ChatMessage[] {
  if (!shouldSupersedeMailDocumentAwaitingContext(prev, incoming)) return prev
  return prev.map((m) => {
    if (!messageHasPendingMailDocumentConfirm(m)) return m
    const md = m.mailDocument
    const nextMd = md
      ? {
          ...md,
          awaitingMailConfirmation: false,
        }
      : md
    return {
      ...m,
      awaitingMailConfirmation: false,
      mailDocument: nextMd,
    }
  })
}

/** Disable blood-test type cards once open-then-mail advances (document opened or mail preview). */
export function applyPrescriptionTypeOptionsResolvedAfterMailFlow(
  prev: ChatMessage[],
  incoming: ChatMessage[],
): ChatMessage[] {
  if (!incomingClosesMailDocumentOpenWait(incoming)) return prev
  let changed = false
  const next = prev.map((m) => {
    if (
      m.role !== 'assistant' ||
      m.historySnapshot ||
      m.uiType !== 'prescription_type_options' ||
      m.prescriptionTypeOptionsResolved ||
      !m.options?.length
    ) {
      return m
    }
    changed = true
    return { ...m, prescriptionTypeOptionsResolved: true }
  })
  return changed ? next : prev
}

export function applyInteractivePreviewSupersedeToEarlierPreviews(
  prev: ChatMessage[],
  incoming: ChatMessage[],
): ChatMessage[] {
  let next = applyReminderConfirmedToEarlierPreviews(prev, incoming)
  next = applyReminderYesNoSupersedeToEarlierPreviews(next, incoming)
  next = applySecretaryPreviewSupersedeToEarlierPreviews(next, incoming)
  next = applyPrescriptionTypeOptionsResolvedAfterMailFlow(next, incoming)
  next = applyMailDocumentOpenAwaitingSupersedeToEarlierPreviews(next, incoming)
  next = applyMailDocumentAwaitingSupersedeToEarlierPreviews(next, incoming)
  return next
}

function reminderIncomingShouldRefreshInPlace(m: ChatMessage): boolean {
  if (m.role !== 'assistant' || !m.reminderPreview || m.historySnapshot) return false
  return m.reminderPreviewUpdateInPlace === true
}

function reminderPreviewCanBeRefreshedInPlace(m: ChatMessage): boolean {
  if (m.role !== 'assistant' || !m.reminderPreview || m.historySnapshot) return false
  if (m.reminderConfirmed || m.reminderCancelled) return false
  if (m.reminderPreviewResolved || m.reminderYesNoSuperseded) return false
  return Boolean(m.awaitingRemindConfirmation || m.awaitingRemindEditField || m.remindFlowActive)
}

function mergeReminderPreviewRefresh(prev: ChatMessage[], incoming: ChatMessage[]): ChatMessage[] | null {
  if (incoming.length === 0) return null

  let refreshIncomingIndex = -1
  for (let i = incoming.length - 1; i >= 0; i--) {
    if (reminderIncomingShouldRefreshInPlace(incoming[i])) {
      refreshIncomingIndex = i
      break
    }
  }
  if (refreshIncomingIndex < 0) return null

  let pendingIndex = -1
  for (let i = prev.length - 1; i >= 0; i--) {
    if (reminderPreviewCanBeRefreshedInPlace(prev[i])) {
      pendingIndex = i
      break
    }
  }
  if (pendingIndex < 0) return null

  const incomingRefresh = incoming[refreshIncomingIndex]
  const base = applyInteractivePreviewSupersedeToEarlierPreviews(prev, incoming)
  const merged: ChatMessage = {
    ...base[pendingIndex],
    ...incomingRefresh,
    id: base[pendingIndex].id,
    awaitingRemindConfirmation: incomingRefresh.awaitingRemindConfirmation,
    awaitingRemindEditField: incomingRefresh.awaitingRemindEditField,
    remindFlowActive: incomingRefresh.remindFlowActive,
    reminderPreviewResolved: false,
    reminderYesNoSuperseded: false,
  }
  const rest = incoming.filter((_, i) => i !== refreshIncomingIndex)
  return [...base.slice(0, pendingIndex), merged, ...base.slice(pendingIndex + 1), ...rest]
}

function secretaryIncomingShouldRefreshInPlace(m: ChatMessage): boolean {
  if (m.role !== 'assistant' || !m.secretaryMessage || m.historySnapshot) return false
  return m.secretaryPreviewUpdateInPlace === true
}

function secretaryPreviewCanBeRefreshedInPlace(m: ChatMessage): boolean {
  if (m.role !== 'assistant' || !m.secretaryMessage || m.historySnapshot) return false
  if (m.secretaryPreviewSuperseded) return false
  return Boolean(m.awaitingMessageConfirmation)
}

function mergeSecretaryPreviewRefresh(prev: ChatMessage[], incoming: ChatMessage[]): ChatMessage[] | null {
  if (incoming.length === 0) return null

  let refreshIncomingIndex = -1
  for (let i = incoming.length - 1; i >= 0; i--) {
    if (secretaryIncomingShouldRefreshInPlace(incoming[i])) {
      refreshIncomingIndex = i
      break
    }
  }
  if (refreshIncomingIndex < 0) return null

  let pendingIndex = -1
  for (let i = prev.length - 1; i >= 0; i--) {
    if (secretaryPreviewCanBeRefreshedInPlace(prev[i])) {
      pendingIndex = i
      break
    }
  }
  if (pendingIndex < 0) return null

  const incomingRefresh = incoming[refreshIncomingIndex]
  const base = applyInteractivePreviewSupersedeToEarlierPreviews(prev, incoming)
  const merged: ChatMessage = {
    ...base[pendingIndex],
    ...incomingRefresh,
    id: base[pendingIndex].id,
    awaitingMessageConfirmation: incomingRefresh.awaitingMessageConfirmation,
    secretaryPreviewSuperseded: false,
    secretaryPreviewUpdateInPlace: false,
  }
  const rest = incoming.filter((_, i) => i !== refreshIncomingIndex)
  const withoutStaleEditPrompts: ChatMessage[] = []
  for (let i = 0; i < base.length; i++) {
    if (i === pendingIndex) {
      withoutStaleEditPrompts.push(merged)
      continue
    }
    const m = base[i]
    if (
      m.role === 'assistant' &&
      !m.historySnapshot &&
      m.awaitingMessageEditField &&
      !m.secretaryMessage
    ) {
      continue
    }
    withoutStaleEditPrompts.push(m)
  }
  return [...withoutStaleEditPrompts, ...rest]
}

function sportsNocIncomingShouldRefreshInPlace(m: ChatMessage): boolean {
  if (m.role !== 'assistant' || m.historySnapshot) return false
  return m.sportsNocPreviewUpdateInPlace === true && m.uiType === 'sports_noc_sports_options'
}

function sportsNocPreviewCanBeRefreshedInPlace(m: ChatMessage): boolean {
  if (m.role !== 'assistant' || m.historySnapshot) return false
  if (m.uiType !== 'sports_noc_sports_options') return false
  if (m.sportsNocSportOptionsResolved) return false
  return m.sportsNocAwaitingField === 'sports'
}

function sportsNocIncomingShouldUpdateSummaryInPlace(m: ChatMessage): boolean {
  if (m.role !== 'assistant' || m.historySnapshot) return false
  return m.sportsNocSummaryUpdateInPlace === true && Boolean(m.sportsNocSummary)
}

function sportsNocGenerateConfirmCanUpdateInPlace(m: ChatMessage): boolean {
  if (m.role !== 'assistant' || m.historySnapshot) return false
  if (!m.sportsNocSummary) return false
  if (m.sportsNocYesNoResolved) return false
  return Boolean(
    m.sportsNocGenerateConfirm ||
      (m.sportsNocAwaitingField === 'generate_confirm' &&
        m.sportsNocYesNoPrompt?.kind === 'generate_certificate'),
  )
}

function mergeSportsNocGenerateConfirmSummaryInPlace(
  prev: ChatMessage[],
  incoming: ChatMessage[],
): ChatMessage[] | null {
  if (incoming.length === 0) return null

  let updateIncomingIndex = -1
  for (let i = incoming.length - 1; i >= 0; i--) {
    if (sportsNocIncomingShouldUpdateSummaryInPlace(incoming[i])) {
      updateIncomingIndex = i
      break
    }
  }
  if (updateIncomingIndex < 0) return null

  let confirmIndex = -1
  for (let i = prev.length - 1; i >= 0; i--) {
    if (sportsNocGenerateConfirmCanUpdateInPlace(prev[i])) {
      confirmIndex = i
      break
    }
  }
  if (confirmIndex < 0) return null

  const incomingUpdate = incoming[updateIncomingIndex]
  const base = applyInteractivePreviewSupersedeToEarlierPreviews(prev, incoming)
  const merged: ChatMessage = {
    ...base[confirmIndex],
    sportsNocSummary: incomingUpdate.sportsNocSummary,
    sportsNocAwaitingField: incomingUpdate.sportsNocAwaitingField ?? 'generate_confirm',
    sportsNocNeedsClarification: false,
    sportsNocGenerateConfirm: true,
    sportsNocYesNoPrompt:
      incomingUpdate.sportsNocYesNoPrompt ?? base[confirmIndex].sportsNocYesNoPrompt,
    sportsNocYesNoResolved: false,
    sportsNocSummaryUpdateInPlace: false,
  }
  const rest = incoming.filter((_, i) => i !== updateIncomingIndex)
  return [...base.slice(0, confirmIndex), merged, ...base.slice(confirmIndex + 1), ...rest]
}

function mergeSportsNocPreviewRefresh(prev: ChatMessage[], incoming: ChatMessage[]): ChatMessage[] | null {
  if (incoming.length === 0) return null

  let refreshIncomingIndex = -1
  for (let i = incoming.length - 1; i >= 0; i--) {
    if (sportsNocIncomingShouldRefreshInPlace(incoming[i])) {
      refreshIncomingIndex = i
      break
    }
  }
  if (refreshIncomingIndex < 0) return null

  let pendingIndex = -1
  for (let i = prev.length - 1; i >= 0; i--) {
    if (sportsNocPreviewCanBeRefreshedInPlace(prev[i])) {
      pendingIndex = i
      break
    }
  }
  if (pendingIndex < 0) return null

  const incomingRefresh = incoming[refreshIncomingIndex]
  const base = applyInteractivePreviewSupersedeToEarlierPreviews(prev, incoming)
  const merged: ChatMessage = {
    ...base[pendingIndex],
    ...incomingRefresh,
    id: base[pendingIndex].id,
    sportsNocAwaitingField: incomingRefresh.sportsNocAwaitingField,
    sportsNocNeedsClarification: incomingRefresh.sportsNocNeedsClarification,
    sportsNocSportSelectedIds:
      incomingRefresh.sportsNocSportSelectedIds ??
      base[pendingIndex].sportsNocSportSelectedIds ??
      [],
    sportsNocSportOptions:
      incomingRefresh.sportsNocSportOptions ?? base[pendingIndex].sportsNocSportOptions,
    sportsNocSportOptionsResolved: false,
    sportsNocPreviewUpdateInPlace: false,
  }
  const rest = incoming.filter((_, i) => i !== refreshIncomingIndex)
  return [...base.slice(0, pendingIndex), merged, ...base.slice(pendingIndex + 1), ...rest]
}

/** Append user message(s) and, if they leave a pending preview flow, disable older actions. */
export function appendChatUserMessages(prev: ChatMessage[], userMsgs: ChatMessage[]): ChatMessage[] {
  if (userMsgs.length === 0) return prev
  let base = prev
  for (const u of userMsgs) {
    if (u.role === 'user') {
      base = applyReminderPreviewInactiveBeforeYesNoFollowUp(base, u.content)
    }
  }
  const marked = applyInteractivePreviewSupersedeToEarlierPreviews(base, userMsgs)
  return [...marked, ...userMsgs]
}

/**
 * When the server returns a patient_options row with `patient_options_refresh`, replace the
 * latest unresolved patient_options message in history instead of appending a duplicate card block.
 */
export function mergePatientOptionsRefresh(prev: ChatMessage[], incoming: ChatMessage[]): ChatMessage[] {
  if (incoming.length === 0) return applyInteractivePreviewSupersedeToEarlierPreviews(prev, incoming)
  const reminderMerged = mergeReminderPreviewRefresh(prev, incoming)
  if (reminderMerged) return reminderMerged
  const secretaryMerged = mergeSecretaryPreviewRefresh(prev, incoming)
  if (secretaryMerged) return secretaryMerged
  const sportsNocSummaryMerged = mergeSportsNocGenerateConfirmSummaryInPlace(prev, incoming)
  if (sportsNocSummaryMerged) return sportsNocSummaryMerged
  const sportsNocMerged = mergeSportsNocPreviewRefresh(prev, incoming)
  if (sportsNocMerged) return sportsNocMerged
  const prev0 = applyInteractivePreviewSupersedeToEarlierPreviews(prev, incoming)

  const refreshIncomingIndex = incoming.findIndex(
    (m) =>
      m.role === 'assistant' &&
      m.uiType === 'patient_options' &&
      m.patientOptionsRefresh === true &&
      m.options &&
      m.options.length > 0,
  )
  if (refreshIncomingIndex < 0) return [...prev0, ...incoming]

  let pendingIndex = -1
  for (let i = prev0.length - 1; i >= 0; i--) {
    const m = prev0[i]
    if (
      m.role === 'assistant' &&
      m.uiType === 'patient_options' &&
      !m.patientOptionsResolved &&
      m.options &&
      m.options.length > 0
    ) {
      pendingIndex = i
      break
    }
  }
  if (pendingIndex < 0) return [...prev0, ...incoming]

  const incomingRefresh = incoming[refreshIncomingIndex]
  const merged: ChatMessage = {
    ...prev0[pendingIndex],
    content: incomingRefresh.content,
    options: incomingRefresh.options,
    patientOptionsPaging: incomingRefresh.patientOptionsPaging,
    patientOptionsRefresh: undefined,
  }
  const rest = incoming.filter((_, i) => i !== refreshIncomingIndex)
  return [...prev0.slice(0, pendingIndex), merged, ...prev0.slice(pendingIndex + 1), ...rest]
}
